import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Menu, Bell, LogOut, ChevronDown } from 'lucide-react';
import apiClient from '../services/apiClient';

interface TopBarProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isDesktop: boolean;
}

export default function TopBar({ isSidebarOpen, setIsSidebarOpen, isDesktop }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const pathTitleMap: Record<string, string> = {
    dashboard: 'Dashboard',
    approvals: 'Approvals Management',
    'pet-owners': 'Pet Owners Management',
    veterinarians: 'Veterinarians Management',
    vendors: 'Vendors Management',
    appointments: 'Appointments Management',
    payments: 'Payments Management',
    'delivery-partners': 'Delivery Partners Management',
    coupons: 'Coupons Management',
    'coupons/new': 'Add Coupons',
    promotions: 'Promotions Management',
    support: 'Support',
    products: 'Products Management',
    orders: 'Orders Management',
    'add _Charge_codes': 'Add Charge Code',
    'pets-details': 'Pet Details',
    notifications: 'Notifications Management',
    reviews: 'Reviews',
    'pet-services': 'Pet Services',
    users: 'Users',
    masters: 'Masters',
    pets: 'Pets',
    'service-providers': 'Services Providers',
    vendor: 'Vendors',
    codes: 'Charge & Tax Codes Management',
    'pet-service-orders': 'Pet Service Orders',
    'notifications/send': 'Send Notifications',
    availability: 'Availability Overview',
    settlements:'Settlements Management',
    sos: 'SOS Emergency',
    '/sos/add': 'Add SOS Emergency',
    'refunds':'Refunds'
  };

  const getTitle = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments[0] === 'pets-details' && segments[1]) return 'Pet Details';
    const path = segments.slice(0, 2).join('/');
    return pathTitleMap[path] || pathTitleMap[segments[0]] || 'Dashboard';
  };

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res: any = await apiClient(
        `v1/resource/notificationLog?recipient_type=Admin&channel=system&status=sent`,
        { method: 'GET' }
      );
      const unseen = (res?.data?.notification_logs || []).filter((n: any) => n.seen !== 'seen');
      setUnreadCount(unseen.length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    setTimeout(() => navigate('/'), 500);
  };

  return (
    <header
      className="h-12 fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 md:px-6 text-white backdrop-blur-md bg-gray-900/80 border-b border-gray-800 shadow-sm transition-all duration-300"
      style={{
        marginLeft: isDesktop && isSidebarOpen ? 192 : 0,
      }}
    >
      {/* Sidebar Toggle (mobile) */}
      {!isDesktop && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-md hover:bg-gray-800/70 transition-colors"
          aria-label="Toggle sidebar"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Page Title */}
      <h2 className="text-base md:text-lg font-semibold tracking-wide select-none text-gray-100 transition-all duration-300">
        {getTitle()}
      </h2>

      {/* Right Side Actions */}
      <div className="flex items-center gap-3 relative">
        {/* Notifications */}
        <div className="relative group">
          <button
            onClick={() => navigate('/notifications')}
            className="p-1.5 rounded-full hover:bg-gray-800/70 transition-colors relative"
            aria-label="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-3.5 h-3.5 text-[9px] font-bold text-white bg-red-600 rounded-full animate-pulse">
                {unreadCount}
              </span>
            )}
          </button>
          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs opacity-0 group-hover:opacity-100 transition bg-gray-800 text-white px-2 py-1 rounded shadow-md">
            Notifications
          </span>
        </div>

        {/* User Avatar + Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-gray-800/70 transition-colors"
          >
            <img
              src="https://ui-avatars.com/api/?name=Admin&background=4f46e5&color=fff"
              alt="User"
              className="w-7 h-7 rounded-full border border-gray-700"
            />
            <ChevronDown size={16} className="text-gray-300" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-1 w-40 bg-gray-900 border border-gray-800 rounded-xl shadow-lg py-1.5">
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-gray-800 transition-colors"
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
